import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import i18n from 'i18next';
import { historyPush, historyReplace } from '../../services/History';
import { liveBattleActionTypes } from './Constants';
import { processRequest } from '../../services/Api';
import { getUserToken, removeUserToken } from './Helpers';
import { battleRoomData } from './Selectors';
import * as errorHandlerActions from '../../components/errorHandler/Actions';
import * as liveBattleActions from './Actions';
import * as notificationActions from '../../components/notification/Actions';

export default function* () {
	yield all([
		yield takeLatest(liveBattleActionTypes.CREATE_LIVE_BATTLE, handleCreateLiveBattleRequest),
		yield takeLatest(
			liveBattleActionTypes.GET_BATTLE_ROOM_DATA,
			handleGetBattleRoomDataRequest,
		),
		yield takeLatest(liveBattleActionTypes.CHECK_OUT_BATTLE, handleCheckOutBattleRequest),
		yield takeLatest(liveBattleActionTypes.CLOSE_BATTLE, handleCloseBattleRequest),
		yield takeLatest(liveBattleActionTypes.CHANGE_BATTLE_SETTINGS, handleChangeSettingsRequest),
		yield takeLatest(liveBattleActionTypes.START_BATTLE, handleStartBattleRequest),
		yield takeLatest(liveBattleActionTypes.GET_ANSWER_RESULTS, handleGetAnswerResultsRequest),
		yield takeLatest(
			liveBattleActionTypes.GET_QUESTION_ANSWERS,
			handleGetQuestionAnswersRequest,
		),
		yield takeLatest(
			liveBattleActionTypes.GO_TO_NEXT_BATTLE_SCREEN,
			handleGoToNextScreenRequest,
		),
		yield takeLatest(liveBattleActionTypes.SKIP_BATTLE_QUESTION, handleSkippQuestionRequest),
	]);
}

export function* handleCreateLiveBattleRequest({ payload }) {
	try {
		const { data: formData } = payload;

		const { data } = yield call(processRequest, 'live_battle_rooms', 'POST', {
			live_battle_room: formData,
		});

		yield call(historyPush, `/live/${data.live_battle_room.code}`);
		yield put(liveBattleActions.createLiveBattleSuccess(data.live_battle_room));
	} catch (e) {
		yield put(liveBattleActions.createLiveBattleError());
		yield put(errorHandlerActions.handleError(e));
	}
}

export function* handleGetBattleRoomDataRequest({ payload }) {
	try {
		const { code, withRedirect } = payload;

		const userToken = getUserToken(code);

		const { data } = yield call(
			processRequest,
			`live_battle_rooms/${code}`,
			'GET',
			{},
			false,
			false,
			userToken ? { 'X-Live-Battle-Room-Token': userToken } : {},
		);

		yield put(liveBattleActions.getBattleRoomDataSuccess(data.live_battle_room));

		if (withRedirect) {
			yield put(liveBattleActions.getBattleRoomDataError(null));
			yield call(historyReplace, `/live/${code}`);
		}
	} catch (e) {
		const { code } = payload;

		if (e.response.status === 404 || e.response.status === 409) {
			yield put(
				liveBattleActions.getBattleRoomDataError({
					code: e.response.status,
					roomCode: code,
					message: e.response.data.error,
				}),
			);
			yield call(historyReplace, '/live');
			return;
		}
		yield put(errorHandlerActions.handleError(e));
	}
}

export function* handleCheckOutBattleRequest({ payload }) {
	try {
		const { roomUserId } = payload;

		const { code, live_battle_room_user } = yield select(battleRoomData) || {};

		const userToken = live_battle_room_user && code && getUserToken(code);

		const { data } = yield call(
			processRequest,
			`live_battle_room_users/${userToken ? live_battle_room_user.id : roomUserId}`,
			'DELETE',
			{},
			false,
			false,
			userToken ? { 'X-Live-Battle-Room-Token': userToken } : {},
		);

		if (userToken) removeUserToken(code);

		yield put(liveBattleActions.checkOutBattleSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(liveBattleActions.checkOutBattleError());
	}
}

export function* handleCloseBattleRequest({ payload }) {
	try {
		const { code } = payload;

		yield call(processRequest, `live_battle_rooms/${code}/close`, 'POST');

		yield put(liveBattleActions.closeBattleSuccess());
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(liveBattleActions.closeBattleError());
	}
}

export function* handleChangeSettingsRequest({ payload }) {
	try {
		const { code, data: formData, onlyLock } = payload;

		const { data } = yield call(processRequest, `live_battle_rooms/${code}`, 'PATCH', {
			live_battle_room: formData,
		});

		if (onlyLock) {
			yield put(
				notificationActions.createNotification(
					i18n.t(
						`live_battle.settings.${
							formData.locked ? 'lock_room_notification' : 'unlock_room_notification'
						}`,
					)
				),
			);
		} else {
			yield put(liveBattleActions.toggleRoomSettingsModal({ open: false }));
		}
		yield put(liveBattleActions.changeBattleSettingsSuccess(data.live_battle_room));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(liveBattleActions.changeBattleSettingsError());
	}
}

export function* handleStartBattleRequest({ payload }) {
	try {
		const { code } = payload;

		const { data } = yield call(processRequest, `live_battle_rooms/${code}/start`, 'POST');

		yield put(liveBattleActions.startBattleSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(liveBattleActions.startBattleError());
	}
}

export function* handleGoToNextScreenRequest({ payload }) {
	try {
		const { code } = payload;
		yield call(processRequest, `live_battle_rooms/${code}/go_to_next_screen`, 'POST');
		yield put(liveBattleActions.goToNextScreenSuccess());
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(liveBattleActions.goToNextScreenError());
	}
}

export function* handleSkippQuestionRequest({ payload }) {
	try {
		const { code } = payload;
		yield call(
			processRequest,
			`live_battle_rooms/${code}/skip_current_question_or_word`,
			'POST',
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleGetAnswerResultsRequest({ payload }) {
	try {
		const { liveBattleRoomId } = payload;
		const userToken = getUserToken(liveBattleRoomId);

		const { data } = yield call(
			processRequest,
			`live_battle_rooms/${liveBattleRoomId}/questions`,
			'GET',
			{},
			false,
			false,
			userToken ? { 'X-Live-Battle-Room-Token': userToken } : {},
		);

		yield put(liveBattleActions.getAnswerResultsSuccess(data.questions));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(liveBattleActions.getAnswerResultsError());
	}
}

export function* handleGetQuestionAnswersRequest({ payload }) {
	const { liveBattleRoomId, questionId } = payload;
	try {
		const { data } = yield call(
			processRequest,
			`live_battle_rooms/${liveBattleRoomId}/questions/${questionId}`,
		);

		yield put(liveBattleActions.getQuestionAnswersSuccess(data.question, questionId));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(liveBattleActions.getQuestionAnswersError(questionId));
	}
}
